import React, { memo } from 'react';
import {
  Avatar,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { StaffMember, StaffTreeModule } from './types';
import { formatInstant, grantedModules, splitMoment } from './staffUtils';
import { STAFF_PRIMARY, kindChipSx, statusChipSx, tableContainerSx, tableHeadSx } from './staffStyles';

interface StaffTableProps {
  rows: StaffMember[];
  tree: StaffTreeModule[];
  /** Список точки пуст (а не отфильтрован поиском) */
  empty: boolean;
  onSelect: (member: StaffMember) => void;
}

/** Колонки, лишние на телефоне: тип и дата создания видны в карточке */
const wideOnly = { display: { xs: 'none', md: 'table-cell' } };
const wideBodyCell = { py: 1, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } };

function onlineTitle(member: StaffMember): string {
  if (member.isOnline) return `Онлайн • В разделе: ${member.currentSection || 'Кабинет'}`;
  if (member.lastActivityAt) return `Офлайн • Был в сети: ${formatInstant(member.lastActivityAt)}`;
  return member.active ? 'Офлайн' : 'Заблокирован';
}

const StaffTable: React.FC<StaffTableProps> = ({ rows, tree, empty, onSelect }) => {
  return (
    <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
      {/* 640 - ширина правой колонки карточки точки в «Пользователях»: таблица входит без прокрутки */}
      <Table sx={{ minWidth: { xs: 0, md: 640 } }}>
        <TableHead sx={tableHeadSx}>
          <TableRow>
            <TableCell sx={{ pl: 2 }}>Сотрудник</TableCell>
            <TableCell sx={wideOnly}>Тип</TableCell>
            <TableCell>Разделы</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell sx={wideOnly}>Создан</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  {empty ? 'Сотрудников пока нет' : 'Сотрудники не найдены'}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((member) => {
              const created = splitMoment(member.createdAt);
              return (
                <TableRow
                  key={member.id}
                  hover
                  onClick={() => onSelect(member)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                    '&:hover': { bgcolor: 'rgba(241, 245, 249, 0.75) !important' },
                    opacity: member.active ? 1 : 0.65,
                    height: 56
                  }}
                >
                  <TableCell sx={{ py: 1, pl: 2, minWidth: 200 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ position: 'relative', flexShrink: 0 }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: member.active ? STAFF_PRIMARY : '#94a3b8',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            border: '2px solid #fff',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.08)'
                          }}
                        >
                          {member.fullName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Tooltip title={onlineTitle(member)} arrow>
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              right: 0,
                              width: 11,
                              height: 11,
                              borderRadius: '50%',
                              bgcolor: member.isOnline ? '#10b981' : (member.active ? '#94a3b8' : '#f97316'),
                              border: '2px solid #fff',
                              boxShadow: member.isOnline ? '0 0 6px #10b981' : 'none'
                            }}
                          />
                        </Tooltip>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', lineHeight: 1.25 }} noWrap>
                          {member.fullName}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748b', fontFamily: 'Consolas, "Roboto Mono", monospace' }} noWrap>
                          {member.login}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={wideBodyCell}>
                    <Chip label={member.kindTitle} size="small" sx={kindChipSx} />
                  </TableCell>
                  <TableCell sx={{ py: 1, whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                      {grantedModules(member.permissions, tree)} из {tree.length}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1, whiteSpace: 'nowrap' }}>
                    <Chip label={member.active ? 'Активен' : 'Заблокирован'} size="small" sx={statusChipSx(member.active)} />
                  </TableCell>
                  <TableCell sx={wideBodyCell}>
                    <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                      {created.date}
                    </Typography>
                    {member.createdBy && (
                      <Typography sx={{ fontSize: '0.74rem', color: '#94a3b8' }} noWrap>
                        {member.createdBy}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default memo(StaffTable);
